import { useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, MessageSquareWarning, X, Eye, HelpCircle, RotateCw, ImagePlus } from "lucide-react";

// Unified recipient response vocabulary for shared reports. "Seen" means the recipient
// indicated they saw the item — it is NOT agreement. Responses can be revised (a new event
// is appended) but are never edited or deleted.
const RESPONSE_OPTIONS = [
  { value: "seen", label: "Seen", icon: Eye, cls: "border-[#CBD5E1] text-[#475569] hover:bg-[#F1F5F9]" },
  { value: "agree", label: "Agree", icon: CheckCircle2, cls: "border-[#A7F3D0] text-[#177245] hover:bg-[#ECFDF5]" },
  { value: "disagree", label: "Disagree", icon: MessageSquareWarning, cls: "border-[#FDE68A] text-[#92400E] hover:bg-[#FFFBEB]" },
  { value: "cannot_verify", label: "Cannot verify", icon: HelpCircle, cls: "border-[#CBD5E1] text-[#475569] hover:bg-[#F1F5F9]" },
];

// Legacy 2-button flow (maintenance links). Unchanged behavior: sends event_type.
function LegacyResponse({ slug, itemRef, itemLabel, testId, endpoint, ackEvent, noteEvent, ackLabel, noteLabel }) {
  const [mode, setMode] = useState(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const url = endpoint ? `${API}${endpoint}` : `${API}/report/${slug}/event`;

  const submit = async () => {
    setBusy(true);
    try {
      const body = { event_type: mode, note: note.trim() || null, signer_name: name.trim() || null };
      if (!endpoint) { body.item_ref = itemRef; body.item_label = itemLabel || null; }
      await axios.post(url, body);
      setDone(mode); setMode(null);
      toast.success("Your response has been recorded with this shared TenantSeal report.");
    } catch { toast.error("Couldn't record your response — please try again."); }
    finally { setBusy(false); }
  };

  if (done) {
    const isAck = done === ackEvent;
    return (
      <div data-testid={`${testId}-recorded`}
        className={`mt-3 rounded-lg border px-3 py-2 flex items-center gap-2 text-xs font-medium ${isAck ? "border-[#A7F3D0] bg-[#ECFDF5] text-[#177245]" : "border-[#FDE68A] bg-[#FFFBEB] text-[#92400E]"}`}>
        {isAck ? <CheckCircle2 size={14} /> : <MessageSquareWarning size={14} />}
        Your response has been recorded.
      </div>
    );
  }
  return (
    <div className="mt-3" data-testid={testId}>
      {!mode ? (
        <div className="flex flex-wrap gap-2">
          <button data-testid={`${testId}-ack-btn`} onClick={() => setMode(ackEvent)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#A7F3D0] bg-white px-3 py-1.5 text-xs font-semibold text-[#177245] hover:bg-[#ECFDF5] transition-colors">
            <CheckCircle2 size={14} /> {ackLabel}
          </button>
          <button data-testid={`${testId}-dispute-btn`} onClick={() => setMode(noteEvent)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#FDE68A] bg-white px-3 py-1.5 text-xs font-semibold text-[#92400E] hover:bg-[#FFFBEB] transition-colors">
            <MessageSquareWarning size={14} /> {noteLabel}
          </button>
        </div>
      ) : (
        <div className="rounded-xl border border-[#E2E8F0] bg-white p-3" data-testid={`${testId}-panel`}>
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-navy">{mode === ackEvent ? "Confirm this response" : "Add a note"}</p>
            <button data-testid={`${testId}-close`} onClick={() => setMode(null)} className="text-[#94A3B8] hover:text-navy"><X size={16} /></button>
          </div>
          <input data-testid={`${testId}-name`} value={name} onChange={(e) => setName(e.target.value)} maxLength={120}
            placeholder="Your name (optional)" className="mt-2 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm outline-none focus:border-brand" />
          <textarea data-testid={`${testId}-note`} value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))} rows={3}
            placeholder={mode === noteEvent ? "Add a note (optional)" : "Add a comment (optional)"}
            className="mt-2 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm outline-none focus:border-brand resize-none" />
          <div className="mt-1 flex items-center justify-between">
            <span className="text-[10px] text-[#94A3B8]">{note.length}/500</span>
            <button data-testid={`${testId}-submit`} onClick={submit} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg bg-navy hover:bg-navy-hover text-white text-xs font-semibold px-4 py-2 transition-colors disabled:opacity-60">
              {busy && <Loader2 size={14} className="animate-spin" />} Record response
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Report flow: 4 options + confirm step + revise (supersede-by-append).
export function RecipientResponse(props) {
  const { slug, itemRef, itemLabel, testId, endpoint } = props;
  const [picked, setPicked] = useState(null); // {value,label}
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recorded, setRecorded] = useState(null); // {label, revised}

  // Maintenance and other legacy callers pass an endpoint → keep the original 2-button flow.
  if (endpoint) return <LegacyResponse {...props} ackEvent={props.ackEvent || "item_acknowledged"}
    noteEvent={props.noteEvent || "noted"} ackLabel={props.ackLabel || "Acknowledge"} noteLabel={props.noteLabel || "Add note"} />;

  const submit = async () => {
    setBusy(true);
    try {
      let responsePhotoId = null;
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        const up = await axios.post(`${API}/report/${slug}/response-photo?item_ref=${encodeURIComponent(itemRef)}`, fd);
        responsePhotoId = up.data.photo_id;
      }
      await axios.post(`${API}/report/${slug}/event`, {
        response: picked.value, item_ref: itemRef, item_label: itemLabel || null,
        signer_name: name.trim() || null, note: note.trim() || null,
        response_photo_id: responsePhotoId,
      });
      setRecorded({ label: picked.label, revised: !!recorded });
      setPicked(null); setNote(""); setPhotoFile(null);
      toast.success("Your response has been recorded.");
    } catch { toast.error("Couldn't record your response — please try again."); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3" data-testid={testId}>
      {recorded && !picked && (
        <div data-testid={`${testId}-recorded`} className="mb-2 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2">
          <p className="text-xs font-medium text-navy">
            You recorded: <b>{recorded.label}</b>{recorded.revised ? " (revised)" : ""} for “{itemLabel}”.
          </p>
          <p className="mt-0.5 text-[11px] text-[#64748B]">This is recorded and can be revised, but is never deleted.</p>
        </div>
      )}

      {!picked ? (
        <div className="flex flex-wrap gap-2">
          {RESPONSE_OPTIONS.map((o) => (
            <button key={o.value} data-testid={`${testId}-${o.value}-btn`} onClick={() => setPicked(o)}
              className={`inline-flex items-center gap-1.5 rounded-lg border bg-white px-3 py-1.5 text-xs font-semibold transition-colors ${o.cls}`}>
              <o.icon size={14} /> {o.label}
            </button>
          ))}
          {recorded && (
            <span className="inline-flex items-center gap-1 text-[11px] text-[#94A3B8]"><RotateCw size={11} /> pick again to revise</span>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-[#E2E8F0] bg-white p-3" data-testid={`${testId}-confirm`}>
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-navy">Confirm your response</p>
            <button data-testid={`${testId}-cancel`} onClick={() => setPicked(null)} className="text-[#94A3B8] hover:text-navy"><X size={16} /></button>
          </div>
          <p className="mt-1.5 text-xs text-[#334155]" data-testid={`${testId}-confirm-text`}>
            You're recording: <b>{picked.label}</b> for <b>{itemLabel}</b>. Responses can be revised but never deleted.
          </p>
          <input data-testid={`${testId}-name`} value={name} onChange={(e) => setName(e.target.value)} maxLength={120}
            placeholder="Your name (optional)" className="mt-2 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm outline-none focus:border-brand" />
          <textarea data-testid={`${testId}-note`} value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))} rows={2}
            placeholder="Add a note (optional)" className="mt-2 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm outline-none focus:border-brand resize-none" />
          <label className="mt-2 flex items-center gap-2 text-[11px] text-[#64748B] cursor-pointer" data-testid={`${testId}-photo-label`}>
            <ImagePlus size={14} className="text-[#94A3B8]" />
            <span>{photoFile ? photoFile.name : "Add a supporting photo (optional)"}</span>
            <input data-testid={`${testId}-photo`} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} />
          </label>
          <p className="mt-1 text-[10px] text-[#94A3B8]">Anything you add here is recorded as <b>your</b> response — separate from the tenant's evidence.</p>
          <div className="mt-1 flex items-center justify-between">
            <span className="text-[10px] text-[#94A3B8]">{note.length}/500</span>
            <button data-testid={`${testId}-submit`} onClick={submit} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg bg-navy hover:bg-navy-hover text-white text-xs font-semibold px-4 py-2 transition-colors disabled:opacity-60">
              {busy && <Loader2 size={14} className="animate-spin" />} Confirm &amp; record
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
